import * as React from "react"
import { Check, ChevronsUpDown, Search } from "lucide-react"
import { cn } from "../../lib/utils"
import { Button } from "./button"
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from "./command"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"

export function SearchableSelect({ 
  options = [], 
  value, 
  onValueChange, 
  placeholder = "Seleccionar...",
  displayValue = null,
  searchPlaceholder = "Buscar...",
  emptyText = "No se encontraron resultados.",
  className,
  disabled = false,
  onOpen = null
}) {
  const [open, setOpen] = React.useState(false)
  const [searchValue, setSearchValue] = React.useState("")
  
  // Handle open state change with optional callback
  const handleOpenChange = (isOpen) => {
    if (isOpen && onOpen) {
      onOpen()
    }
    setOpen(isOpen)
  }
  
  // Buscar la opción seleccionada
  const selectedLabel = React.useMemo(() => {
    if (displayValue) return displayValue
    if (value === null || value === undefined || value === '') return null
    const strValue = String(value)
    const found = options.find(opt => String(opt.value) === strValue)
    return found ? found.label : null
  }, [value, options, displayValue])
  
  const filteredOptions = React.useMemo(() => {
    let filtered = options
    if (searchValue) {
      const search = searchValue.toLowerCase()
      filtered = options.filter(opt => 
        opt.label.toLowerCase().includes(search)
      )
    }
    return filtered.slice(0, 100)
  }, [options, searchValue])

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("justify-between font-normal overflow-hidden", className)}
          disabled={disabled}
        >
          <span className={cn("truncate block max-w-[calc(100%-24px)]", !selectedLabel && "text-muted-foreground")}>
            {selectedLabel || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command shouldFilter={false}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup className="max-h-[300px] overflow-auto">
              {filteredOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={String(option.value)}
                  onSelect={() => {
                    const newValue = String(option.value)
                    onValueChange(newValue === String(value) ? "" : newValue)
                    setOpen(false)
                    setSearchValue("")
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      String(value) === String(option.value) ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
